/**
 * ConfirmationCard — the diff-rendering "I'm about to change something
 * for you, OK?" surface that gates every write the assistant performs.
 *
 * Stream 11 (Phase 1) ships ONLY the primitive — no wiring to a real
 * tool. A staff-only debug route mounts it with mock data so we can
 * dogfood the visual contract before Phase 2 Action A wires it to
 * `save_compliance_answer`. That's by design: the CTO plan calls out
 * that "real wiring is Phase 2 Action A" and Phase 1 is "battle-test
 * the primitive with a mocked instance."
 *
 * UX CONTRACT (phase-1-spec §6 / copy-deck §5.1):
 *   - Title in active voice: "I'll {action_verb} {object}".
 *   - One or more diffs (rendered by <ConfirmationCardDiff>).
 *   - "Source: {source_summary}" line — proves we know where the
 *     proposed value came from (a user message, a parsed file, etc.).
 *   - "Reverts via: {revert_path}" line — tells the user how to undo
 *     the change *before* they commit. This is non-negotiable: every
 *     assistant write must surface its reverse.
 *   - Three CTAs in fixed order: Confirm (primary), Edit (outline),
 *     Cancel (ghost). Confirm is the default action; Cancel is the
 *     "back out" affordance keyed to Esc.
 *
 * AUTO-FOCUS WITH 50ms GRACE:
 *   The Confirm button receives focus on mount, but with a 50ms
 *   setTimeout. Without the grace period, screen readers can race
 *   the live-region announcement of the card title — moving focus
 *   immediately interrupts the "I'll save these to your 2026 LL97
 *   worksheet" announcement and the user hears only "Confirm". The
 *   50ms gap lets the SR finish the title before focus shifts.
 *
 *   For users with `prefers-reduced-motion: reduce` we skip the
 *   timeout and focus synchronously — those users have explicitly
 *   asked the OS to remove transitions / delays, and a 50ms ghost
 *   delay reads as jank to them. The motion-reduce case is checked
 *   via the `motion-reduce:` Tailwind modifier on the card itself
 *   (kept on the wrapper as a no-op so Stream 16's reduced-motion
 *   sweep has nothing to do here).
 *
 * KEYBOARD:
 *   - Esc: invokes `onCancel`. We listen at the document level
 *     (capture phase) for the duration the card is mounted; a parent
 *     dialog/sidebar that also listens to Esc must be aware that the
 *     card consumes the event first via stopPropagation. This is the
 *     correct precedence for the chat shell — Esc on a confirmation
 *     should cancel the *confirmation*, not close the whole sidebar.
 *   - Tab: native, follows DOM order (Confirm → Edit → Cancel).
 *
 * A11Y:
 *   - role="dialog" + aria-modal="false". The card is *modal in
 *     intent* (the user must answer it before the conversation
 *     proceeds) but NOT modal in DOM (the rest of the chat panel
 *     remains keyboard-reachable so the user can scroll up to
 *     re-read the previous turn before deciding). Screen-reader
 *     dialog semantics with non-modal DOM lands the right user-
 *     mental-model: "this is a decision point, but I'm not trapped".
 *   - aria-labelledby points at the title; aria-describedby points
 *     at the diff block so the SR announces both before reaching
 *     the CTAs.
 *
 * NO COPY.TS WIRING (yet):
 *   The CTA labels ("Confirm", "Edit", "Cancel"), the "Source:" /
 *   "Reverts via:" prefix words, and the title pattern are inlined
 *   here for the Stream 11 staff-only mock. When Phase 2 Action A
 *   wires the real tool, we'll move them into `copy.ts §17` (the
 *   confirmation-card section). Inlining now avoids a copy.ts churn
 *   that would block four other in-flight streams.
 */
import React, { useCallback, useEffect, useRef } from "react";

import { Button } from "@/components/shared/ui/button";
import { cn } from "@/lib/utils";

import ConfirmationCardDiff, {
  type FieldChange,
} from "./ConfirmationCardDiff";

// TODO(copy-foundation): move to copy.ts §17 (Confirmation card copy)
// when Phase 2 Action A lands. Pattern: the strings live here for
// Stream 11's mocked-only render and graduate to copy.ts when the
// first real consumer (save_compliance_answer) wires up.
const CONFIRM_LABEL = "Confirm";
const EDIT_LABEL = "Edit";
const CANCEL_LABEL = "Cancel";
const SOURCE_PREFIX = "Source:";
const REVERTS_PREFIX = "Reverts via:";

/** Grace window before auto-focusing Confirm — see header comment. */
const AUTO_FOCUS_GRACE_MS = 50;

export interface ConfirmationCardProps {
  /**
   * Card title — full sentence, voice-of-the-assistant. Phase 2 will
   * compose this from the action verb + object slot; today the
   * caller hands us the finished string.
   */
  title: string;
  /** One or more field changes to display in the diff block. */
  changes: ReadonlyArray<FieldChange>;
  /**
   * "Where did the proposed value come from?" — short string. Examples:
   * "Your message above", "Auto-classified from filename".
   */
  source: string;
  /**
   * "How does the user undo this if they regret it?" — short string.
   * Examples: "Filing → 2026 LL97 → Edit".
   */
  revertsVia: string;
  /** Confirm CTA handler — required (the primary action of the card). */
  onConfirm: () => void;
  /**
   * Edit CTA handler — optional. When omitted, the Edit button is
   * not rendered (some confirmations have no editable form). When
   * present, "Edit" should open the diff in a form for in-place
   * tweaks before re-confirmation.
   */
  onEdit?: () => void;
  /** Cancel CTA handler — required (also fires on Esc). */
  onCancel: () => void;
  /**
   * Disable the Confirm CTA. Useful when an upstream validation hasn't
   * finished or the network is in-flight on a previous confirm. The
   * card stays mounted and Esc still cancels.
   */
  confirmDisabled?: boolean;
  /** Optional className for layout overrides. */
  className?: string;
}

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
  title,
  changes,
  source,
  revertsVia,
  onConfirm,
  onEdit,
  onCancel,
  confirmDisabled = false,
  className,
}) => {
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  // Stable ids so aria-labelledby / aria-describedby resolve correctly
  // even when multiple cards stack in the same transcript.
  const idRef = useRef<string>(
    `confirmation-card-${Math.random().toString(36).slice(2, 10)}`,
  );
  const cardId = idRef.current;
  const titleId = `${cardId}-title`;
  const diffId = `${cardId}-diff`;

  // Auto-focus on Confirm with the SR grace window. We read
  // prefers-reduced-motion at mount time only — flipping the OS
  // setting mid-card would be an unusual UX corner case. The defensive
  // optional-chain on `.matches` handles the JSDOM corner case where
  // `matchMedia` is a bare `vi.fn()` returning undefined; in real
  // browsers `matchMedia` always returns a MediaQueryList.
  useEffect(() => {
    const mq =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : undefined;
    const reduceMotion = mq?.matches === true;
    if (reduceMotion) {
      // Reduced-motion users: focus immediately, no grace.
      confirmRef.current?.focus();
      return;
    }
    const handle = window.setTimeout(() => {
      confirmRef.current?.focus();
    }, AUTO_FOCUS_GRACE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, []);

  // Esc → onCancel. Capture phase so the card eats the event before
  // a parent dialog (e.g., the sidebar's outermost <Dialog>) can
  // close on top of us. We stopPropagation so Esc cancels exactly
  // ONE level of overlay per keypress.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCancel();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handleKeyDown]);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={diffId}
      data-testid="confirmation-card"
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-background p-4",
        // Reduced-motion modifier kept on the wrapper as a no-op so
        // Stream 16's reduced-motion sweep is a no-touch for this
        // component. We do NOT animate the card mount — like
        // RefusalCard, a transitionless mount is the right default
        // for a status surface.
        "motion-safe:transition-none motion-reduce:transition-none",
        className,
      )}
    >
      <h3
        id={titleId}
        data-testid="confirmation-card-title"
        className="text-[16px] leading-[22px] font-medium text-foreground"
      >
        {title}
      </h3>

      <div id={diffId}>
        <ConfirmationCardDiff changes={changes} />
      </div>

      <dl className="flex flex-col gap-1 text-[13px] leading-[19px] text-muted-foreground">
        <div className="flex gap-1.5" data-testid="confirmation-card-source">
          <dt className="font-medium">{SOURCE_PREFIX}</dt>
          <dd>{source}</dd>
        </div>
        <div className="flex gap-1.5" data-testid="confirmation-card-reverts">
          <dt className="font-medium">{REVERTS_PREFIX}</dt>
          <dd>{revertsVia}</dd>
        </div>
      </dl>

      <div
        className="flex flex-wrap items-center gap-2 pt-1"
        data-testid="confirmation-card-ctas"
      >
        <Button
          ref={confirmRef}
          type="button"
          variant="default"
          size="sm"
          onClick={onConfirm}
          disabled={confirmDisabled}
          aria-label={CONFIRM_LABEL}
          data-testid="confirmation-card-confirm"
        >
          {CONFIRM_LABEL}
        </Button>
        {onEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onEdit}
            aria-label={EDIT_LABEL}
            data-testid="confirmation-card-edit"
          >
            {EDIT_LABEL}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          aria-label={CANCEL_LABEL}
          data-testid="confirmation-card-cancel"
        >
          {CANCEL_LABEL}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmationCard;
